import { useEffect, useState } from "react";
import { Sheet } from "../../components/Sheet";
import { Checkbox, Field, NumberInput, Segmented } from "../../components/fields";
import { Picker, MultiPicker, type PickerOption } from "../../components/Picker";
import { Button } from "../../components/ui";
import { useArtists, useTaxonomy, useVocabulary } from "../../api/queries";
import { activeFilterCount, emptyFilters, type Filters } from "./filterState";
import { fieldLabels } from "../../i18n/fieldLabels";
import { pick } from "../../i18n/labels";
import { useT } from "../../i18n/strings";
import type { Term } from "../../api/types";
import s from "./filters.module.css";

const termOptions = (terms: Term[] | undefined): PickerOption[] =>
  (terms ?? []).map((term) => ({
    id: term.id,
    fa: term.label_fa,
    en: term.label_en,
    isSeeded: term.is_seeded,
  }));

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  filters: Filters;
  onApply: (filters: Filters) => void;
}

/**
 * Edits a draft and commits on Apply, so half-typed year bounds do not refetch
 * the list on every keystroke.
 */
export function FilterSheet({ open, onClose, filters, onApply }: FilterSheetProps) {
  const t = useT();
  const [draft, setDraft] = useState<Filters>(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const taxonomy = useTaxonomy();
  const mediums = useVocabulary("medium");
  const styles = useVocabulary("style");
  const ownership = useVocabulary("ownership_status");
  const condition = useVocabulary("condition_status");
  const artists = useArtists("");

  const types: PickerOption[] = (taxonomy.data ?? []).map((type) => ({
    id: type.id,
    fa: type.label_fa,
    en: type.label_en,
    isSeeded: type.is_seeded,
  }));

  // Subtypes are scoped to their parent: Painting→Watercolor is not the same
  // row as Works on Paper→Watercolor, so the list is never global.
  const parent = taxonomy.data?.find((type) => type.id === draft.type);
  const subtypes: PickerOption[] = (parent?.subtypes ?? []).map((subtype) => ({
    id: subtype.id,
    fa: subtype.label_fa,
    en: subtype.label_en,
    isSeeded: subtype.is_seeded,
  }));

  const set = (changes: Partial<Filters>) => setDraft((current) => ({ ...current, ...changes }));
  const count = activeFilterCount(draft);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t.filters.title}
      headerAction={
        count > 0 ? (
          <button
            type="button"
            className={s.clear}
            onClick={() => setDraft({ ...emptyFilters, q: draft.q })}
          >
            {t.actions.clearAll}
          </button>
        ) : null
      }
      footer={
        <Button
          variant="primary"
          onClick={() => {
            onApply(draft);
            onClose();
          }}
        >
          {count > 0 ? `${t.actions.apply} · ${count}` : t.actions.apply}
        </Button>
      }
    >
      <div className={s.body}>
        <Field label={fieldLabels.type} optional>
          <Picker
            title={pick(fieldLabels.type)}
            options={types}
            value={draft.type}
            placeholder={t.browse.any}
            // Dropping the type strands a subtype that no longer applies.
            onChange={(id) => set({ type: id, subtype: null })}
          />
        </Field>

        <Field label={fieldLabels.subtype} optional>
          <Picker
            title={pick(fieldLabels.subtype)}
            options={subtypes}
            value={draft.subtype}
            disabled={!draft.type}
            placeholder={draft.type ? t.browse.any : t.browse.pickTypeFirst}
            onChange={(id) => set({ subtype: id })}
          />
        </Field>

        <Field label={fieldLabels.artist} optional>
          <Picker
            title={pick(fieldLabels.artist)}
            options={(artists.data ?? []).map((artist) => ({
              id: artist.id,
              fa: artist.name,
              en: "",
              count: artist.artwork_count,
            }))}
            value={draft.artist}
            placeholder={t.browse.any}
            emptyText={t.form.noArtists}
            onChange={(id) => set({ artist: id })}
          />
        </Field>

        <Field label={fieldLabels.year} optional>
          <div className={s.yearRow}>
            <NumberInput
              value={draft.year_from}
              step="1"
              placeholder={t.filters.yearFrom}
              onChange={(value) => set({ year_from: value })}
            />
            <span className={s.yearDash}>–</span>
            <NumberInput
              value={draft.year_to}
              step="1"
              placeholder={t.filters.yearTo}
              onChange={(value) => set({ year_to: value })}
            />
          </div>
          {/* A range filter cannot compare a work with no year, so unknown-year
              works are opted in explicitly rather than silently dropped. */}
          <Checkbox
            checked={draft.year_unknown}
            onChange={(checked) => set({ year_unknown: checked })}
          >
            {t.filters.unknownYear}
          </Checkbox>
        </Field>

        <Field label={fieldLabels.mediums} optional hint={t.filters.anyOf}>
          <MultiPicker
            title={pick(fieldLabels.mediums)}
            options={termOptions(mediums.data)}
            values={draft.medium}
            placeholder={t.browse.any}
            onChange={(ids) => set({ medium: ids })}
          />
        </Field>

        <Field label={fieldLabels.style} optional>
          <Picker
            title={pick(fieldLabels.style)}
            options={termOptions(styles.data)}
            value={draft.style}
            placeholder={t.browse.any}
            onChange={(id) => set({ style: id })}
          />
        </Field>

        <Field label={fieldLabels.ownership_status} optional>
          <Picker
            title={pick(fieldLabels.ownership_status)}
            options={termOptions(ownership.data)}
            value={draft.ownership_status}
            placeholder={t.browse.any}
            onChange={(id) => set({ ownership_status: id })}
          />
        </Field>

        <Field label={fieldLabels.condition_status} optional>
          <Picker
            title={pick(fieldLabels.condition_status)}
            options={termOptions(condition.data)}
            value={draft.condition_status}
            placeholder={t.browse.any}
            onChange={(id) => set({ condition_status: id })}
          />
        </Field>

        <Field label={fieldLabels.primary_media} optional>
          <Segmented
            name="has-media"
            value={
              draft.has_primary_media === null ? "any" : draft.has_primary_media ? "yes" : "no"
            }
            options={[
              { value: "any", label: t.browse.any },
              { value: "yes", label: t.filters.hasMedia },
              { value: "no", label: t.detail.noMedia },
            ]}
            onChange={(value) =>
              set({ has_primary_media: value === "any" ? null : value === "yes" })
            }
          />
        </Field>
      </div>
    </Sheet>
  );
}
